import { pluckValue, isNotNullable } from '@bayer-int/portletce-shared';
import { invert } from 'lodash-es';

import type { Settings } from 'portlet/models';

import { TitleSettingDefinition } from './titleSetting';
import { CurrentViewSettingDefinition } from './currentViewSetting';
import { ScaleSettingDefinition } from './scaleSetting';
import { InternalKeySettingDefinition } from './internalKeySetting';
import { HierarchyIdSettingDefinition } from './hierarchySetting';
import { SingleSelectSettingDefinition } from './singleSelectSetting';
import { LabelDisplayStyleSettingDefinition } from './labelDisplayStyleSetting';
import { HierarchyVariantIdSettingDefinition } from './hierarchyVariantSetting';
import { SmartViewSettingDefinition } from './smartViewSetting';
import { SortSettingDefinition } from './sortSetting';
import { InputBWQuerySettingDefinition } from './inputBWQuery';
import { CustomSettingFormDefinitionInput } from '../customSettingFormDefinitionInput';

const SettingDefinitionsThatCanBeMandatory = {
    TITLE: TitleSettingDefinition,
    VIEWID: CurrentViewSettingDefinition,
    SCALE: ScaleSettingDefinition,
    INTERNAL_KEY: InternalKeySettingDefinition,
    HIERARCHY_ID: HierarchyIdSettingDefinition,
    HIERARCHY_VARIANT_ID: HierarchyVariantIdSettingDefinition,
    SINGLE_SELECT: SingleSelectSettingDefinition,
    LABEL_DISPLAY_STYLE: LabelDisplayStyleSettingDefinition,
    SMART_VIEW: SmartViewSettingDefinition,
    SORT: SortSettingDefinition,
    INPUT_BW_QUERY: InputBWQuerySettingDefinition,
} as const;

export const SettingThatCanBeMandatoryKeys = pluckValue(
    SettingDefinitionsThatCanBeMandatory,
    'key',
);
export const SettingThatCanBeMandatoryLabels = invert(
    pluckValue(SettingDefinitionsThatCanBeMandatory, 'label'),
);

export function getMandatorySettingKeyBasedOnLabel(
    settings: Partial<Settings>,
    label: string,
): string | null {
    const canBeMandatory = !!SettingThatCanBeMandatoryLabels[label];
    const key = SettingThatCanBeMandatoryLabels[label];

    if (canBeMandatory) return key;

    const potentialCustomSetting = settings[label];

    // for custom settings we treat the `settings.<property_name>` as the key
    if (potentialCustomSetting && CustomSettingFormDefinitionInput.codec.is(potentialCustomSetting))
        return label;

    return null;
}

export function getListOfMandatorySettingOptions(
    settings: Partial<Settings>,
): Array<{ key: string; label: string }> {
    return Object.keys(settings)
        .map(label => {
            const key = getMandatorySettingKeyBasedOnLabel(settings, label);
            if (!key) return null;

            return { key, label };
        })
        .filter(isNotNullable);
}
